import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import { Menu, X, Mail, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import CornemuseIcon from "@/components/CornemuseIcon";

export const NAV_LINKS = [
  { to: "/", label: "Accueil", end: true },
  { to: "/prestations", label: "Prestations" },
  { to: "/stages", label: "Stages" },
  { to: "/galerie", label: "Galerie" },
  { to: "/audio", label: "Audio" },
  { to: "/actualites", label: "Actualités" },
  { to: "/association", label: "L'Association" },
  { to: "/contact", label: "Contact" },
  { to: "/mentions-legales", label: "Mentions légales" },
];

function QuoteBanner() {
  const [quotes, setQuotes] = useState([]);
  const [offset, setOffset] = useState(0); // avance manuelle via la petite flèche

  useEffect(() => {
    api
      .get("/quotes")
      .then((r) => setQuotes(r.data || []))
      .catch(() => {});
  }, []);

  // Une seule citation par jour : l'index dépend de la date et boucle sur le stock de citations
  const dayIndex = Math.floor(Date.now() / 86400000);
  const idx = quotes.length ? (((dayIndex + offset) % quotes.length) + quotes.length) % quotes.length : 0;
  const quote = quotes[idx];
  if (!quote) return null;
  return (
    <div data-testid="daily-quote-banner" className="bg-[#B83227] px-3 py-1.5 text-white">
      <div className="mx-auto flex max-w-6xl items-center gap-3">
        {/* Bulle façon bande dessinée avec la cornemuse du groupe */}
        <span
          aria-hidden="true"
          className="relative flex h-9 w-10 shrink-0 -rotate-6 items-center justify-center rounded-[50%] border-2 border-[#1E1B18] bg-white text-[#B83227] shadow-[2px_2px_0_rgba(0,0,0,0.35)]"
        >
          <CornemuseIcon className="h-5 w-5" />
          {/* queue de la bulle */}
          <span className="absolute -bottom-[7px] left-1.5 h-3 w-3 rotate-45 rounded-br-[3px] border-b-2 border-r-2 border-[#1E1B18] bg-white" />
        </span>
        <div className="min-w-0 flex-1 leading-snug">
          <span className="block text-[9px] font-bold uppercase tracking-widest text-amber-200/90">La citation cornemuse du jour</span>
          <p key={idx} className="text-[11px] sm:text-xs">
            <span className="italic">«&nbsp;{quote.text}&nbsp;»</span>{" "}
            <span className="text-[10px] font-bold uppercase tracking-wide text-amber-200">— {quote.source}</span>
          </p>
        </div>
        {quotes.length > 1 && (
          <button
            data-testid="quote-next-button"
            onClick={() => setOffset((o) => o + 1)}
            aria-label="Citation suivante"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-black/25 text-white transition-colors duration-200 hover:bg-black/45"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function SiteHeader() {
  const [open, setOpen] = useState(false);
  const { settings } = useSite();
  const siteTitle = settings?.site_title || "Les Sonneurs de la Côte";
  const tagline = settings?.site_tagline || "Cornemuses du Centre-France & Bal Trad";
  const logoUrl = settings?.logo_url || "/logo-sonneurs.jpg";
  // Le slogan rouge ne dépasse jamais la largeur du titre : sa taille est mesurée et ajustée
  const titleRef = useRef(null);
  const tagRef = useRef(null);
  useLayoutEffect(() => {
    const fit = () => {
      const title = titleRef.current;
      const tag = tagRef.current;
      if (!title || !tag) return;
      tag.style.fontSize = ""; // repart de la taille CSS de base
      const maxW = title.clientWidth;
      if (!maxW || tag.scrollWidth <= maxW) return;
      const base = parseFloat(getComputedStyle(tag).fontSize);
      tag.style.fontSize = `${Math.max(5.5, base * (maxW / tag.scrollWidth) * 0.98)}px`;
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [siteTitle, tagline]);
  const linkClass = ({ isActive }) =>
    `text-xs font-bold uppercase transition-colors duration-300 hover:text-[#B83227] ${
      isActive ? "text-[#B83227]" : "text-stone-700"
    }`;
  return (
    <header className="sticky top-0 z-40 border-b border-stone-200 bg-[#FAF6F0]/95 backdrop-blur">
      <div className="mx-auto flex h-24 max-w-7xl items-center justify-between px-4 sm:px-6 lg:h-28">
        <Link to="/" data-testid="site-logo-link" className="flex items-center gap-3.5">
          <img
            src={logoUrl}
            alt={`Logo ${siteTitle}`}
            data-testid="site-logo-image"
            className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-[#B83227]/20 sm:h-16 sm:w-16 lg:h-20 lg:w-20"
          />
          <div className="min-w-0">
            <span ref={titleRef} className="block whitespace-nowrap font-display text-[clamp(1.05rem,5.4vw,1.5rem)] font-bold uppercase leading-none tracking-tight text-[#1E1B18]">
              {siteTitle}
            </span>
            <span ref={tagRef} className="block whitespace-nowrap text-[clamp(0.35rem,1.8vw,0.625rem)] font-bold uppercase tracking-widest text-[#B83227]">
              {tagline}
            </span>
          </div>
        </Link>
        <nav data-testid="nav-menu-desktop" className="hidden items-center gap-4 xl:flex xl:gap-5">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={linkClass}>
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="whitespace-nowrap rounded bg-[#B83227] px-4 py-2.5 text-xs font-bold uppercase text-white shadow transition-colors duration-300 hover:bg-[#96281E]"
          >
            {settings?.texts?.nav_cta_label || "Réserver le groupe"}
          </Link>
        </nav>
        <button
          data-testid="nav-menu-toggle"
          className="rounded-md p-1.5 text-[#1E1B18] xl:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Ouvrir le menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <nav data-testid="nav-menu-mobile" className="border-t border-stone-200 bg-[#FAF6F0] px-4 py-4 xl:hidden">
          <div className="flex flex-col gap-3">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} className={linkClass}>
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-button-mobile"
              onClick={() => setOpen(false)}
              className="mt-1 rounded bg-[#B83227] px-4 py-2.5 text-center text-sm font-bold uppercase text-white"
            >
              {settings?.texts?.nav_cta_label || "Réserver le groupe"}
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}

function SiteFooter() {
  const { settings } = useSite();
  const siteTitle = settings?.site_title || "Les Sonneurs de la Côte";
  return (
    <footer className="border-t border-stone-800 bg-[#1E1B18] py-12 text-white">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex flex-col items-start justify-between gap-6 border-b border-stone-700 pb-8 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <img
              src={settings?.logo_url || "/logo-sonneurs.jpg"}
              alt={`Logo ${siteTitle}`}
              className="h-16 w-16 rounded-full object-cover ring-2 ring-stone-700"
            />
            <div>
              <span className="block font-display text-2xl font-bold uppercase tracking-tight">
                {siteTitle}
              </span>
              <span className="mt-1 block text-xs text-stone-400">
                {settings?.footer_text || "Musique traditionnelle du Centre-France pour vos bals et cérémonies"}
              </span>
            </div>
          </div>
          <nav className="flex flex-wrap gap-6 text-xs font-bold uppercase text-stone-300">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="transition-colors duration-300 hover:text-white">
                {l.label}
              </Link>
            ))}
            <Link to="/contact" className="text-[#E67E22] transition-colors duration-300 hover:text-white">
              Devis
            </Link>
          </nav>
        </div>
        <div className="flex flex-col items-center justify-between gap-3 pt-6 text-xs text-stone-500 sm:flex-row">
          <p>© {new Date().getFullYear()} {siteTitle} · Association loi 1901 · Bals trad &amp; animations vivantes</p>
          <div className="flex items-center gap-4">
            <Link to="/mentions-legales" data-testid="footer-legal-link" className="hover:text-stone-300">
              Mentions légales
            </Link>
            <Link to="/admin" data-testid="footer-admin-link" className="inline-flex items-center gap-1.5 hover:text-stone-300">
              <Mail className="h-3.5 w-3.5" aria-hidden="true" /> Espace membres
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="flex min-h-screen flex-col">
      <QuoteBanner />
      <SiteHeader />
      <main className="flex-1">
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}
